import React, { useContext, useEffect, useState } from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useParams, useLocation } from "react-router-dom";

import { Typography, Grid, Divider, Link, Button } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { uniq } from "lodash";
import { PSPIcon } from "../../components/Icons/PSPIcon";
import { SectionGridContainer } from "../../components/Layout";
import { LoadingOverlay } from "../../components/Navigation/LoadingOverlay";
import { RiderContext } from "../../context";
import { StringUtils } from "../../util";
import {
  DebitTransactionTypes,
  TransactionsTypesForCashout,
  DEFAULT_PSP_ICON,
  PSP,
} from "../../constants";
import {
  FailedBrainTreeTransactionRenderer,
  TransactionRowRenderer,
} from "./subcomponents/TransactionColumnHelper";
import { hexToRgba } from "../../services/misc.service";
import { useTestIds } from "../../hooks/useTestIds";
import Table from "../../components/Layout/Table";
import PaymentProfileLink from "./subcomponents/PaymentProfileLink";
import getPaymentProfileUrl from "../../util/strings/getPaymentProfileUrl";

const { formatNumber } = StringUtils;

const useStyles = makeStyles((theme) => ({
  dividerPad: {
    paddingTop: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
  bonusValue: {
    opacity: 0.5,
    lineHeight: 1,
  },
  transactionsCell: {
    verticalAlign: "top",
    background: theme.palette.background.paper,
  },
  transactionsAmountCell: {
    textAlign: "right",
    verticalAlign: "top",
    background: theme.palette.background.paper,
  },
  reservationLink: {
    borderBottom: `1px dashed ${hexToRgba(theme.palette.text.primary, 50)}`,
    cursor: "pointer",

    "&:hover": {
      textDecoration: "none",
      borderBottomColor: hexToRgba(theme.palette.text.primary, 80),
    },
    color: theme.palette.text.primary,
  },
}));

const Transactions = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const location = useLocation();
  const { t } = useTranslation();
  const { riderId } = useParams();
  const testIds = useTestIds("rider-profile-transactions-page");
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentFloor, setFloor] = useState(0);
  const [currentCeiling, setCeiling] = useState(15);
  const [slicedRows, setSlicedRows] = useState([]);
  const rowsPerPageOptions = [15, 30];

  const { wallet, walletTransactions, fetchWalletTransactions } = useContext(RiderContext);

  const currentTimezone = DateTime.fromISO(new Date().toISOString()).offsetNameShort;

  const paymentProviders = uniq(walletTransactions?.map((wt) => wt.payment_provider)) || [];
  const showPaypalLink = [PSP.paypal, PSP.braintree].some((provider) =>
    paymentProviders.includes(provider),
  );

  useEffect(() => {
    handleFetchTransactions();
    return () => {
      fetchWalletTransactions(null);
    };
  }, [location, riderId]);

  const handleFetchTransactions = async () => {
    try {
      await fetchWalletTransactions(riderId);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    setCurrentPage(0);
    setCeiling(currentFloor + rowsPerPage);
  }, [rowsPerPage]);

  useEffect(() => {
    setFloor(currentPage * rowsPerPage);
  }, [currentPage]);

  useEffect(() => {
    setCeiling(currentFloor + rowsPerPage);
  }, [currentFloor]);

  useEffect(() => {
    if (walletTransactions) {
      setSlicedRows(walletTransactions.slice(currentFloor, currentCeiling));
    }
  }, [currentFloor, currentCeiling, walletTransactions]);

  const getPSPIcon = (paymentProvider) => {
    const icon = paymentProvider || DEFAULT_PSP_ICON;
    return <PSPIcon provider={`${icon}.svg`} />;
  };

  if (!walletTransactions) {
    return <LoadingOverlay />;
  }

  const shouldGroupRow = (row, rowIndex) => {
    const nextRow = slicedRows[rowIndex + 1];
    return (
      (row.reservation_id || row.group_reservation_id) &&
      (row.reservation_id === nextRow?.reservation_id ||
        (row.group_reservation_id !== null &&
          row.group_reservation_id === nextRow?.group_reservation_id))
    );
  };
  const tableColumns = [
    {
      canonicalName: "created",
      name: `${t("Riders.WalletTransactions.Date")}, ${currentTimezone}`,
      tableCellProps: { className: classes.transactionsCell },
      renderCellContent: (row) =>
        DateTime.fromISO(row.created).toLocaleString(DateTime.DATETIME_MED),
    },
    {
      canonicalName: "reference_type",
      name: t("Riders.WalletTransactions.Type"),
      tableCellProps: { className: classes.transactionsCell },
      renderCellContent: (row) => (
        <>
          {t(`Riders.TransactionTypes.${row.reference_type}`)}
          {row.payment_provider === PSP.braintree && row.status === "failed" && (
            <FailedBrainTreeTransactionRenderer />
          )}
          {(row.status === "failed" ||
            row.status === "abandoned" ||
            // Specific case for a wallet transaction that has no amount nor bonus_amount that is intentionally allowed through the filter in RiderState
            (row.status === "canceled" &&
              row.reference_type === "CARD_HOLD" &&
              row.amount === 0 &&
              row.bonus_amount === 0)) && <TransactionRowRenderer transaction={row} />}
        </>
      ),
    },
    {
      canonicalName: "payment_provider",
      name: t("Riders.WalletTransactions.Source"),
      tableCellProps: { className: classes.transactionsCell },
      renderCellContent: (row) => getPSPIcon(row.payment_provider),
    },
    {
      canonicalName: "payment_method",
      name: t("Riders.WalletTransactions.CardNumber"),
      tableCellProps: { className: classes.transactionsCell },
      renderCellContent: (row) => row.payment_method?.last4,
    },
    {
      canonicalName: "amount",
      name: "",
      customHeader: (
        <>
          {t("Riders.WalletTransactions.DepositAmount")}
          <br />
          <Typography className={classes.bonusValue}>
            {t("Riders.WalletTransactions.BonusAmount")}
          </Typography>
        </>
      ),
      tableHeaderProps: { className: classes.transactionsAmountCell },
      tableCellProps: { className: classes.transactionsAmountCell },
      renderCellContent: (row) => (
        <>
          <Typography>
            {(DebitTransactionTypes.includes(row.reference_type) && row.amount ? "- " : "") +
              formatNumber(row.amount, row.currency || wallet?.currency)}
          </Typography>
          <Typography className={classes.bonusValue}>
            {(DebitTransactionTypes.includes(row.reference_type) && row.bonus_amount ? "- " : "") +
              formatNumber(row.bonus_amount, row.currency || wallet?.currency)}
          </Typography>
        </>
      ),
    },
    {
      canonicalName: "group_reservation_id" || "reservation_id",
      name: t("Riders.WalletTransactions.References"),
      tableCellProps: { className: classes.transactionsCell },
      renderCellContent: (row, index) => {
        if (row.group_reservation_id) {
          return (
            row.group_reservation_id !==
              walletTransactions[currentFloor + index - 1]?.group_reservation_id && (
              <Link
                href={`/riders/${riderId}/group-reservations/${row.group_reservation_id}`}
                className={classes.reservationLink}
                target="_blank"
                data-testid={`${testIds.referencesLink}-${index}`}
              >
                GROUP RESERVATION
              </Link>
            )
          );
        }
        if (row.reservation_id) {
          return (
            row.reservation_id !== walletTransactions[currentFloor + index - 1]?.reservation_id && (
              <Link
                href={`/riders/${riderId}/reservations/${row.reservation_id}`}
                className={classes.reservationLink}
                target="_blank"
                data-testid={`${testIds.referencesLink}-${index}`}
              >
                RESERVATION
              </Link>
            )
          );
        }
        return null;
      },
    },
    {
      canonicalName: "actions",
      name: t("Riders.WalletTransactions.Actions"),
      tableCellProps: { className: classes.transactionsCell },
      renderCellContent: (row, index) => {
        if (
          row.status !== "failed" &&
          row.status !== "abandoned" &&
          TransactionsTypesForCashout.includes(row.reference_type)
        ) {
          return (
            <Button
              component={RouterLink}
              to={{
                pathname: `/riders/${riderId}/wallet/transactions/${row.id}/cash-out`,
                state: { background: location },
              }}
              variant="outlined"
              data-testid={`${testIds.cashOutButton}-${index}`}
            >
              {t("Riders.WalletTransactions.CashOut")}
            </Button>
          );
        }
        return null;
      },
    },
  ];

  return (
    <SectionGridContainer spaceContents style={{ paddingLeft: 0, paddingRight: 0 }}>
      {walletTransactions && paymentProviders.length && (
        <Grid item container justify="flex-end" xs={12}>
          {paymentProviders.includes(PSP.payments_os) && (
            <PaymentProfileLink
              url={getPaymentProfileUrl("PaymentsOS", riderId)}
              label={t("Riders.Wallet.PaymentsOS")}
              testId={testIds.paymentsOsProfileLink}
            />
          )}
          {showPaypalLink && (
            <PaymentProfileLink
              url={getPaymentProfileUrl("Paypal")}
              label={t("Riders.Wallet.Paypal")}
              testId={testIds.paypalProfileLink}
            />
          )}
          {paymentProviders.includes(PSP.stripe) && (
            <PaymentProfileLink
              url={getPaymentProfileUrl("Stripe", riderId)}
              label={t("Riders.Wallet.Stripe")}
              testId={testIds.stripeProfileLink}
            />
          )}
        </Grid>
      )}
      <Grid item xs={12} className={classes.dividerPad}>
        <Divider />
      </Grid>
      <Grid item xs={12} className={classes.dividerPad}>
        <Table
          rows={slicedRows}
          columns={tableColumns}
          shouldGroupRow={shouldGroupRow}
          muiTableProps={{ stickyHeader: true }}
          paginationProps={{
            rowsPerPage,
            rowsPerPageOptions,
            pageNumber: currentPage,
            totalRowsCount: walletTransactions?.length,
            setPageNumber: setCurrentPage,
            setRowsPerPage,
          }}
        />
      </Grid>
    </SectionGridContainer>
  );
};

export default Transactions;
